import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import IconButton, { StyledActionGroup } from "./IconButton";
import { useDropdownPosition, useOutsideClick } from "./useDropdownBehavior";

const StyledTrigger = styled.div({
    display: "inline-flex",
});

const StyledMenu = styled.div(({ $top = 0, $left, $right }) => ({
    position: "fixed",
    top: $top,
    left: $left,
    right: $right,
    minWidth: "160px",
    display: "flex",
    flexDirection: "column",
    padding: tokens.globals["spacing-1"],
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.hover,
    zIndex: 1600,
}));

const StyledMenuItem = styled.button({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2-5"]}`,
    border: "none",
    background: "transparent",
    borderRadius: tokens.globals["border-radius"],
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    textAlign: "left",
    cursor: "pointer",

    "i": { fontSize: tokens.typography.fontSize["md-12px"], width: "14px" },
    "&:hover:not(:disabled)": { background: tokens.colors.neutral.grey.Alpha["10"] },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
});

/**
 * @typedef {Object} IconAction
 * @property {string} icon Font Awesome icon name.
 * @property {string} ariaLabel Accessible name.
 * @property {string} [label] Text for the collapsed menu item (falls back to `ariaLabel`); unused when shown as a direct button.
 * @property {"outline"|"solid"|"destructive"|"ghost"} [variant] See `IconButton`'s `variants` map; default "outline".
 * @property {Function} onClick
 * Also: disabled, disabledReason, title (self-explanatory).
 */

/**
 * Row of action icons for a card/row. By default the first action shows directly and every other
 * action collapses behind a "..." menu, including when that leaves just one item in it; pass
 * `collapseAfterFirst={false}` to show every action directly instead, with no menu at all.
 * @param {IconAction[]} props.actions Actions in priority order — the first stays direct, the
 *   rest go in the menu (when collapsing).
 * @param {boolean} [props.collapseAfterFirst] Whether actions after the first collapse into a
 *   "..." menu (default) or show directly alongside it.
 */
function IconActionGroup({ actions = [], className, collapseAfterFirst = true }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const menuPosition = useDropdownPosition(containerRef, isOpen, { align: "right" });

    useOutsideClick(containerRef, listRef, isOpen, () => setIsOpen(false));

    const visibleActions = collapseAfterFirst ? actions.slice(0, 1) : actions;
    const collapsedActions = collapseAfterFirst ? actions.slice(1) : [];

    return (
        <StyledActionGroup className={className}>
            {visibleActions.map((action) => (
                <IconButton
                    key={action.ariaLabel || action.icon}
                    icon={action.icon}
                    ariaLabel={action.ariaLabel}
                    variant={action.variant}
                    disabled={action.disabled}
                    disabledReason={action.disabledReason}
                    title={action.title}
                    onClick={action.onClick}
                />
            ))}

            {collapsedActions.length > 0 && (
                <StyledTrigger ref={containerRef}>
                    <IconButton
                        icon="ellipsis-h-alt"
                        ariaLabel="More actions"
                        onClick={(event) => {
                            if (event) {
                                event.stopPropagation();
                            }
                            setIsOpen((open) => !open);
                        }}
                    />

                    {isOpen && createPortal(
                        <StyledMenu ref={listRef} role="menu" $top={menuPosition.top} $left={menuPosition.left} $right={menuPosition.right}>
                            {collapsedActions.map((action) => (
                                <StyledMenuItem
                                    key={action.ariaLabel || action.icon}
                                    role="menuitem"
                                    type="button"
                                    disabled={action.disabled}
                                    title={action.disabled ? action.disabledReason : action.title}
                                    onClick={(event) => {
                                        if (event) {
                                            event.stopPropagation();
                                        }
                                        setIsOpen(false);
                                        action.onClick(event);
                                    }}
                                >
                                    <i className={`fal fa-${action.icon}`} aria-hidden="true" />
                                    <span>{action.label || action.ariaLabel}</span>
                                </StyledMenuItem>
                            ))}
                        </StyledMenu>,
                        document.body
                    )}
                </StyledTrigger>
            )}
        </StyledActionGroup>
    );
}

IconActionGroup.displayName = "IconActionGroup";

export default IconActionGroup;
